import { dcAuthApi } from '../apiBase';
import type { CreateTenantInput, Tenant, TenantType } from '../../types/tenant';

interface TenantDto {
  id: string;
  name: string;
  email: string;
  code: string;
  type: string;
  schema_profile: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

function mapTenantType(apiType: string): TenantType {
  return apiType === 'organization' ? 'ORGANIZATION' : 'PERSONAL';
}

function mapTenantTypeToApi(type: TenantType): string {
  return type === 'ORGANIZATION' ? 'organization' : 'personal';
}

function mapTenant(dto: TenantDto): Tenant {
  return {
    id: dto.id,
    name: dto.name,
    email: dto.email,
    code: dto.code,
    type: mapTenantType(dto.type),
    schemaProfile: dto.schema_profile ?? undefined,
    isPrimary: false,
    createdAt: dto.created_at ?? '',
    updatedAt: dto.updated_at ?? '',
  };
}

export interface TenantServiceAccount {
  userId: string;
  clientId?: string;
  name: string;
  email: string;
  username: string;
  isServiceAccount: boolean;
  clientSecret?: string;
}

interface TenantServiceAccountDto {
  userId: string;
  clientId?: string | null;
  name: string;
  email: string;
  username: string;
  isServiceAccount: boolean;
  clientSecret?: string | null;
}

export interface CreateTenantServiceAccountInput {
  clientId: string;
  name: string;
  email?: string;
  username?: string;
}

function mapServiceAccount(dto: TenantServiceAccountDto): TenantServiceAccount {
  return {
    userId: dto.userId,
    clientId: dto.clientId ?? undefined,
    name: dto.name,
    email: dto.email,
    username: dto.username,
    isServiceAccount: dto.isServiceAccount,
    clientSecret: dto.clientSecret ?? undefined,
  };
}

export const tenantApi = {
  async listManaged(): Promise<Tenant[]> {
    const response = await dcAuthApi.get<TenantDto[]>('/tenants/managed');
    return response.data.map(mapTenant);
  },

  async list(): Promise<Tenant[]> {
    const response = await dcAuthApi.get<TenantDto[]>('/tenants');
    return response.data.map(mapTenant);
  },

  async get(id: string): Promise<Tenant | undefined> {
    try {
      const response = await dcAuthApi.get<TenantDto>(`/tenants/${id}`);
      return mapTenant(response.data);
    } catch (error) {
      if (error instanceof Error && error.message.includes('not found')) {
        return undefined;
      }
      throw error;
    }
  },

  async create(input: CreateTenantInput): Promise<Tenant> {
    const response = await dcAuthApi.post<TenantDto>('/tenants', {
      name: input.name,
      email: input.email,
      code: input.code,
      type: mapTenantTypeToApi(input.type),
    });
    return mapTenant(response.data);
  },

  async setSchemaProfile(tenantId: string, schemaProfile: string): Promise<Tenant> {
    const response = await dcAuthApi.put<TenantDto>(`/tenants/${tenantId}/schema-profile`, {
      schema_profile: schemaProfile,
    });
    return mapTenant(response.data);
  },

  async listServiceAccounts(tenantId: string): Promise<TenantServiceAccount[]> {
    const response = await dcAuthApi.get<TenantServiceAccountDto[]>(
      `/tenants/${tenantId}/service-accounts`,
    );
    return response.data.map(mapServiceAccount);
  },

  async createServiceAccount(
    tenantId: string,
    input: CreateTenantServiceAccountInput,
  ): Promise<TenantServiceAccount> {
    const response = await dcAuthApi.post<TenantServiceAccountDto>(
      `/tenants/${tenantId}/service-accounts`,
      {
        client_id: input.clientId,
        name: input.name,
        email: input.email,
        username: input.username,
      },
    );
    return mapServiceAccount(response.data);
  },
};
